import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useFormattingContext } from '../../FormattingProvider/FormattingContext'
import { useDenaliFilter } from '../filters/DenaliFilterContextProvider'
import { useDenaliData, useFirmReportConfiguration } from '../../../../api/coreData'
import { useHideIfNoDataWhen } from '../../HideIfNoData'
import DebouncedInput from '../../../molecules/DebouncedInput'
import OpalGrid, { useGridPageState, useGridSortState } from '../tables/operational'
import DenaliOpReportFilterPicker from './DenaliOpReportFilterPicker'

const mapAccessorKey = column => column.accessorKey ? column.accessorKey : column.accessor
const mapSize = column => column.size ? column.size : column.width
const mapMaxSize = column => column.maxSize ? column.maxSize : column.maxWidth
const mapMinSize = column => column.minSize ? column.minSize : column.minWidth
const mapHeader = column => column.header ? column.header : column.Header

export const useColumns = (columns, totals) => {
  const { formatter } = useFormattingContext()

  return useMemo(() => {
    const includeFooter = totals?.length > 0
    const templatedColumns = columns.map((column) => {
      const result = {
        ...column,
        header: mapHeader(column),
        accessorKey: mapAccessorKey(column),
        size: mapSize(column),
        maxSize: mapMaxSize(column),
        minSize: mapMinSize(column)
      }
      if (!result?.cell) {
        result.cell = (props) => formatter(props.getValue(), column.format)
      }
      if (includeFooter && !result?.footer) {
        const total = totals.at(0)
        result.footer = () => formatter(total[mapAccessorKey(column)], column.format)
      }

      return result
    })

    return {
      columns: templatedColumns,
      includeFooter
    }
  }, [formatter, columns, totals])
}

const useDenaliDataQuery = ({ reportId, baseQuery, totalQuery, pageState, sortState, resetPageState }) => {
  const { filter, membershipFilter } = useDenaliFilter()
  const [textFilter, _setTextFilter] = useState({})
  const [baseFilter, _setBaseFilter] = useState(null)
  const setTextFilter = useCallback(filter => {
    _setTextFilter(filter)
    resetPageState()
  }, [resetPageState, _setTextFilter])
  const setBaseFilter = useCallback(baseFilter => {
    _setBaseFilter(baseFilter)
    resetPageState()
  }, [resetPageState, _setBaseFilter])

  const query = useMemo(() => {
    const q = [{
      key: 'data',
      req: {
        ...baseQuery,
        ...(baseFilter ? { baseFilter } : {}),
        membershipFilter,
        filters: {
          ...(filter || {}),
          ...(baseQuery?.filters || {}),
          ...(textFilter || {})
        },
        reportId,
        take: pageState.pageSize,
        skip: pageState.pageSize * pageState.pageIndex,
        sortBy: (sortState || []).map(s => ({ field: s.id, dir: s.desc ? 'desc' : 'asc' }))
      }
    }]

    if (totalQuery) {
      q.push({
        key: 'total',
        req: {
          ...totalQuery,
          ...(baseFilter ? { baseFilter } : {}),
          membershipFilter,
          filters: {
            ...(filter || {}),
            ...(baseQuery?.filters || {}),
            ...(textFilter || {})
          },
          reportId
        }
      })
    }

    return q
  }, [reportId, baseQuery, totalQuery, filter, membershipFilter, pageState, textFilter, baseFilter, sortState])

  const result = useDenaliData(query, { keepPreviousData: true })
  return {
    data: result?.data,
    isLoading: result?.isLoading,
    isFetching: result?.isFetching,
    textFilter,
    setTextFilter,
    baseFilter,
    setBaseFilter
  }
}

function DenaliOpReport ({
  reportId,
  baseQuery,
  totalQuery,
  filterGroupTag,
  columnConfig,
  pageSize,
  className,
  style,
  defaultSort,
  componentClasses,
  componentStyles
}) {
  const { pageState, setPageState, resetPageState } = useGridPageState(pageSize)
  const { sortState, setSortState } = useGridSortState(defaultSort)
  const { data: configuration, isLoading: configurationLoading } = useFirmReportConfiguration(reportId)
  const { data, isLoading, isFetching, setTextFilter, baseFilter, setBaseFilter } = useDenaliDataQuery({
    reportId, baseQuery, totalQuery, pageState, sortState, resetPageState
  })
  const { columns } = useColumns(columnConfig, data?.total || [])
  const onMegaSearchChange = useCallback(v => {
    const textSearch = Object.entries(configuration.columns).filter(([, x]) => x.search === true).reduce((prev, [key]) => {
      if (v) {
        prev[key] = [{ op: 'contains', value: v }]
      }
      return prev
    }, {})

    setTextFilter(textSearch)
  }, [configuration, setTextFilter])

  // Used with <HideIfNoData />
  useHideIfNoDataWhen(() => !isLoading && !data?.data?.length, [data, isLoading])

  return (
    <div className={className} style={style}>
      <div className='flex flex-row justify-between items-center mb-1'>
        <div>
          <DenaliOpReportFilterPicker
            value={baseFilter}
            onChange={setBaseFilter}
            configuration={configuration}
            isLoading={configurationLoading}
            filterGroupTag={filterGroupTag}
          />
        </div>
        <div>
          <DebouncedInput
            placeholder='Search'
            onChange={onMegaSearchChange}
          />
        </div>
      </div>
      <div>
        <OpalGrid
          componentClasses={componentClasses}
          componentStyles={componentStyles}
          loading={isLoading}
          data={data?.data || []}
          columns={columns}
          rowCount={data?.total?.[0]?.count}
          isFetching={isFetching}
          defaultPageSize={pageSize}
          pageState={pageState}
          onPageChange={setPageState}
          sortState={sortState}
          onSortChange={setSortState}
        />
      </div>
    </div>
  )
}

DenaliOpReport.propTypes = {
  /** The denali analysis report code */
  reportId: PropTypes.string,

  /** The base denali analysis query */
  baseQuery: PropTypes.object,

  /** The query to get the totals */
  totalQuery: PropTypes.object,

  /** Column configuration (v2) */
  columnConfig: PropTypes.array,

  /** Only show filters with a particular filterGroupTag */
  filterGroupTag: PropTypes.string,

  /** Default sort state of the grid */
  defaultSort: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    desc: PropTypes.bool
  })),

  /** Page Size */
  pageSize: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
  componentClasses: PropTypes.shape({
    table: PropTypes.string,
    header: PropTypes.string,
    cell: PropTypes.string,
    footer: PropTypes.string
  }),
  componentStyles: PropTypes.shape({
    table: PropTypes.object,
    header: PropTypes.object,
    cell: PropTypes.object,
    footer: PropTypes.object
  })
}

DenaliOpReport.defaultProps = {
  pageSize: 100,
  defaultSort: []
}

export default DenaliOpReport
